import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import * as validators from '../../../util/validators';
import { getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';

import { Form, PrimaryButton, FieldTextInput, CustomExtendedDataField } from '../../../components';

import FieldSelectUserType from '../FieldSelectUserType';
import UserFieldDisplayName from '../UserFieldDisplayName';
import UserFieldPhoneNumber from '../UserFieldPhoneNumber';

import css from './SignupForm.module.css';

const getSoleUserTypeMaybe = userTypes =>
  Array.isArray(userTypes) && userTypes.length === 1 ? userTypes[0].userType : null;

const isPasswordUsedMoreThanOnce = formValues => {
  const pw = formValues.password;
  const hasPasswordString = pw != null && pw.length >= validators.PASSWORD_MIN_LENGTH;
  if (hasPasswordString) {
    const isPasswordRepeated = Object.values(formValues).filter(v => v === pw).length > 1;
    return isPasswordRepeated;
  }
  return false;
};

const ROLE_CTA = {
  buyer: 'Start buying',
  seller: 'List my vehicle',
  dealer: 'Create dealership account',
};

const EyeOpen = () => (
  <svg
    width="19"
    height="19"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOff = () => (
  <svg
    width="19"
    height="19"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const SignupFormComponent = props => {
  const [showPassword, setShowPassword] = useState(false);

  const resolvedUserType = props.preselectedUserType || getSoleUserTypeMaybe(props.userTypes);

  return (
    <FinalForm
      {...props}
      key={resolvedUserType || 'default'}
      mutators={{ ...arrayMutators }}
      initialValues={{ userType: resolvedUserType }}
      render={formRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          handleSubmit,
          inProgress,
          invalid,
          intl,
          termsAndConditions,
          preselectedUserType,
          userTypes,
          userFields,
          values,
          selectedRole,
        } = formRenderProps;

        const { userType } = values || {};

        const emailRequired = validators.required(
          intl.formatMessage({ id: 'SignupForm.emailRequired' })
        );
        const emailValid = validators.emailFormatValid(
          intl.formatMessage({ id: 'SignupForm.emailInvalid' })
        );

        const passwordRequiredMessage = intl.formatMessage({ id: 'SignupForm.passwordRequired' });
        const passwordMinLengthMessage = intl.formatMessage(
          { id: 'SignupForm.passwordTooShort' },
          { minLength: validators.PASSWORD_MIN_LENGTH }
        );
        const passwordMaxLengthMessage = intl.formatMessage(
          { id: 'SignupForm.passwordTooLong' },
          { maxLength: validators.PASSWORD_MAX_LENGTH }
        );
        const passwordMinLength = validators.minLength(
          passwordMinLengthMessage,
          validators.PASSWORD_MIN_LENGTH
        );
        const passwordMaxLength = validators.maxLength(
          passwordMaxLengthMessage,
          validators.PASSWORD_MAX_LENGTH
        );
        const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);
        const passwordValidators = validators.composeValidators(
          passwordRequired,
          passwordMinLength,
          passwordMaxLength
        );

        const userFieldProps = getPropsForCustomUserFieldInputs(userFields, userType);
        const noUserTypes = !userType && !(userTypes?.length > 0);
        const userTypeConfig = userTypes.find(config => config.userType === userType);
        const showDefaultUserFields = userType || noUserTypes;
        const showCustomUserFields = (userType || noUserTypes) && userFieldProps?.length > 0;

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = inProgress;
        const submitDisabled = invalid || submitInProgress || isPasswordUsedMoreThanOnce(values);
        const ctaLabel = intl.formatMessage({ id: 'SignupForm.signUp' });
        const passwordId = formId ? `${formId}.password` : 'password';

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <FieldSelectUserType
              name="userType"
              userTypes={userTypes}
              hasExistingUserType={!!preselectedUserType}
              intl={intl}
            />

            {showDefaultUserFields ? (
              <div className={css.defaultUserFields}>
                <FieldTextInput
                  type="email"
                  id={formId ? `${formId}.email` : 'email'}
                  name="email"
                  autoComplete="email"
                  label={intl.formatMessage({ id: 'SignupForm.emailLabel' })}
                  placeholder={intl.formatMessage({ id: 'SignupForm.emailPlaceholder' })}
                  validate={validators.composeValidators(emailRequired, emailValid)}
                />
                <div className={css.name}>
                  <FieldTextInput
                    className={css.firstNameRoot}
                    type="text"
                    id={formId ? `${formId}.fname` : 'fname'}
                    name="fname"
                    autoComplete="given-name"
                    label={intl.formatMessage({ id: 'SignupForm.firstNameLabel' })}
                    placeholder={intl.formatMessage({ id: 'SignupForm.firstNamePlaceholder' })}
                    validate={validators.required(
                      intl.formatMessage({ id: 'SignupForm.firstNameRequired' })
                    )}
                  />
                  <FieldTextInput
                    className={css.lastNameRoot}
                    type="text"
                    id={formId ? `${formId}.lname` : 'lname'}
                    name="lname"
                    autoComplete="family-name"
                    label={intl.formatMessage({ id: 'SignupForm.lastNameLabel' })}
                    placeholder={intl.formatMessage({ id: 'SignupForm.lastNamePlaceholder' })}
                    validate={validators.required(
                      intl.formatMessage({ id: 'SignupForm.lastNameRequired' })
                    )}
                  />
                </div>

                <UserFieldDisplayName
                  formName="SignupForm"
                  className={css.row}
                  userTypeConfig={userTypeConfig}
                  intl={intl}
                />

                {/* Password with eye toggle */}
                <div className={css.passwordWrapper}>
                  <FieldTextInput
                    key={showPassword ? 'pw-text' : 'pw-pass'}
                    className={css.password}
                    type={showPassword ? 'text' : 'password'}
                    id={passwordId}
                    name="password"
                    autoComplete="new-password"
                    label={intl.formatMessage({ id: 'SignupForm.passwordLabel' })}
                    placeholder={intl.formatMessage({ id: 'SignupForm.passwordPlaceholder' })}
                    validate={passwordValidators}
                  />
                  <button
                    type="button"
                    className={css.eyeBtn}
                    onClick={() => setShowPassword(v => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOpen /> : <EyeOff />}
                  </button>
                </div>

                <UserFieldPhoneNumber
                  formName="SignupForm"
                  className={css.row}
                  userTypeConfig={userTypeConfig}
                  intl={intl}
                />
              </div>
            ) : null}

            {showCustomUserFields ? (
              <div className={css.customFields}>
                {userFieldProps.map(({ key, ...fieldProps }) => (
                  <CustomExtendedDataField key={key} {...fieldProps} formId={formId} />
                ))}
              </div>
            ) : null}

            <div className={css.bottomWrapper}>
              {termsAndConditions}
              {isPasswordUsedMoreThanOnce(values) ? (
                <div className={css.error}>
                  <FormattedMessage id="SignupForm.passwordRepeatedOnOtherFields" />
                </div>
              ) : null}
              <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
                {ctaLabel}
              </PrimaryButton>
            </div>
          </Form>
        );
      }}
    />
  );
};

const SignupForm = props => {
  const intl = useIntl();
  return <SignupFormComponent {...props} intl={intl} />;
};

export default SignupForm;
